import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { base44 } from "@/api/base44Client";
import { useAuth } from "@/lib/AuthContext";

/**
 * AuthExchange — handles SSO landings from the IdP (the "Other Emergent App").
 *
 * Expected URL formats:
 *   /auth/exchange#auth_token=<jwt-or-opaque>
 *   /auth/exchange?token=<jwt-or-opaque>
 *
 * Flow:
 *   1. Read the token from the URL hash (preferred) or query string.
 *   2. POST /api/auth/exchange { token } — backend calls the IdP's verify
 *      endpoint, mints a TA Fitness JWT.
 *   3. Store JWT in localStorage and refresh AuthContext.
 *   4. Send the user to "/" (or to ?next= if provided).
 */
export default function AuthExchange() {
  const navigate = useNavigate();
  const { checkUserAuth } = useAuth();
  const processed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const url = new URL(window.location.href);
    const hashMatch = (window.location.hash || "").match(/auth_token=([^&]+)/);
    const token = hashMatch
      ? decodeURIComponent(hashMatch[1])
      : url.searchParams.get("token");
    const next = url.searchParams.get("next") || "/";

    if (!token) {
      setError("No SSO token was provided.");
      return;
    }

    (async () => {
      try {
        const { data } = await base44.http.post("/auth/exchange", { token });
        if (data?.access_token) {
          // The shim's http instance does NOT auto-store tokens; mirror what
          // loginViaEmailPassword does.
          window.localStorage.setItem("ta_access_token", data.access_token);
        }
        // Strip the token from history so a refresh can't replay it
        window.history.replaceState(null, "", "/");
        await checkUserAuth();
        navigate(next.startsWith("/") ? next : "/", { replace: true });
      } catch (e) {
        setError(e?.message || "Single sign-on failed");
      }
    })();
  }, [navigate, checkUserAuth]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="text-center max-w-sm" data-testid="auth-exchange">
        {error ? (
          <>
            <p className="text-destructive font-semibold mb-2">Sign-in failed</p>
            <p className="text-sm text-muted-foreground mb-4">{error}</p>
            <button
              className="text-primary text-sm underline"
              onClick={() => navigate("/login", { replace: true })}
            >
              Back to login
            </button>
          </>
        ) : (
          <>
            <div className="w-10 h-10 border-4 border-primary/20 border-t-primary rounded-full animate-spin mx-auto mb-4" />
            <p className="text-sm text-muted-foreground">Signing you in via Gota Auth…</p>
          </>
        )}
      </div>
    </div>
  );
}
